import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { scrollableContainerStyle } from '../styles/ScrollableContainer';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Badge,
  InputBase,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Avatar,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Slider,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  ShoppingCart as CartIcon,
  Chat as ChatIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  Add as AddIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Close as XIcon,
  LocalOffer as OfferIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  FilterList as FilterListIcon,
  Male,
  Female,
  KeyboardArrowDown as ArrowDownIcon,
  MyLocation,
  CheckCircle,
  PhotoCamera,
  Face
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import BottomNavigation from '../components/BottomNavigation';
import EnhancedLocationDialog from '../components/EnhancedLocationDialog';
import TryOnDialog from '../components/TryOnDialog';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContextNew';
import { useChatUnread } from '../contexts/ChatUnreadContext';
import SignUpPromptModal from '../components/SignUpPromptModal';
import { homeAPI, productAPI } from '../services/api';
import notificationService from '../services/notificationService';
import useResponsive from '../hooks/useResponsive';
import BrandLoader from '../components/BrandLoader';
import { formatCurrency, getCountryCode, ensureSyncCountryCode } from '../utils/countryUtils';
import { formatDateShort } from '../utils/timezoneUtils';


// Styled components
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  width: 48,
  height: 48,
  margin: theme.spacing(0, 1),
  '&:hover': {
    transform: 'scale(1.1)',
  },
  transition: 'all 0.3s ease',
}));

/** Not shown on Home category UI (exact catalog names, case-insensitive). Provider profile unchanged. */
const HIDDEN_HOME_CATEGORY_NAMES = new Set(['offers', 'deals', 'packages']);

function filterCategoriesForHomeDisplay(categories) {
  if (!Array.isArray(categories)) return [];
  return categories.filter(
    (c) => !HIDDEN_HOME_CATEGORY_NAMES.has(String(c.name || '').trim().toLowerCase())
  );
}

/** Match Recommended Salons/Pros filters to API shapes (providerType, type, casing). */
function getHomeProviderKind(p) {
  const raw = p?.providerType ?? p?.type ?? p?.provider_type;
  return typeof raw === 'string' ? raw.trim().toLowerCase() : '';
}

/**
 * `homeAPI.getAllData` returns axios `response.data` (full JSON body). Providers may be at
 * `data.providers`, top-level `providers`, or nested `data.data.providers`.
 */
function extractProvidersFromHomeAllDataResponse(res) {
  if (!res || typeof res !== 'object') return [];
  const inner = res.data && typeof res.data === 'object' ? res.data : res;
  if (Array.isArray(inner.providers)) return inner.providers;
  if (
    inner.data &&
    typeof inner.data === 'object' &&
    Array.isArray(inner.data.providers)
  ) {
    return inner.data.providers;
  }
  if (Array.isArray(res.providers)) return res.providers;
  return [];
}

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { 
    isMobile: isResponsiveMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getResponsiveGrid 
  } = useResponsive();
  const { totalItems, addToCart, isInCart, getItemQuantity, updateQuantity, isItemInCart, getItemQuantityInCart, updateItemQuantity, removeFromCart, items } = useCart();
  
  const { isGuest } = useAuth();
  const { unreadCount: chatCount, refreshUnreadCount } = useChatUnread();

  
  // State for various features
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('Current Location');
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  
  // State for banners and categories
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // State for providers (salons and pros)
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  
  // State for Try On dialog
  const [tryOnDialogOpen, setTryOnDialogOpen] = useState(false);
  const [tryOnCategory, setTryOnCategory] = useState('haircut');
  
  // Get saved location from localStorage on component mount
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    const savedAddress = localStorage.getItem('userAddress');
    
    console.log('🔍 Loading saved location:', { savedLocation, savedAddress });
    
    if (savedLocation) {
      try {
        const locationData = JSON.parse(savedLocation);
        console.log('📍 Parsed location data:', locationData);
        
        // Priority: fullAddress > address > savedAddress
        const addressToDisplay = locationData.fullAddress || locationData.address || savedAddress;
        console.log('🏠 Address to display:', addressToDisplay);
        
        if (addressToDisplay && addressToDisplay !== 'Current Location' && addressToDisplay !== 'Current Location (GPS)') {
          setSelectedAddress(addressToDisplay);
          setLocation(addressToDisplay);
          console.log('✅ Set location to:', addressToDisplay);
        } else if (addressToDisplay) {
          // If it's "Current Location" or "Current Location (GPS)", keep it
          setSelectedAddress(addressToDisplay);
          setLocation(addressToDisplay);
          console.log('📍 Set location to current location:', addressToDisplay);
        } else {
          // No valid address found, use fallback
          setSelectedAddress('Current Location');
          setLocation('Current Location');
          console.log('⚠️ No valid address found, using fallback');
        }
      } catch (error) {
        console.error('Error parsing saved location:', error);
        // Fallback to savedAddress if parsing fails
        if (savedAddress && savedAddress !== 'Current Location' && savedAddress !== 'Current Location (GPS)') {
          setSelectedAddress(savedAddress);
          setLocation(savedAddress);
          console.log('🔄 Fallback to savedAddress:', savedAddress);
        } else {
          setSelectedAddress('Current Location');
          setLocation('Current Location');
          console.log('⚠️ Using fallback after error');
        }
      }
    } else if (savedAddress && savedAddress !== 'Current Location' && savedAddress !== 'Current Location (GPS)') {
      // Fallback to savedAddress if no userLocation JSON
      setSelectedAddress(savedAddress);
      setLocation(savedAddress);
      console.log('🔄 Using savedAddress as fallback:', savedAddress);
    } else {
      // No saved location data, use default
      setSelectedAddress('Current Location');
      setLocation('Current Location');
      console.log('📍 No saved location, using default');
    }
  }, []);

  // Listen for location updates from EnhancedLocationDialog
  useEffect(() => {
    const handleLocationUpdate = (event) => {
      console.log('🔄 Location update received:', event.detail);
      const { address, fullAddress } = event.detail;
      const displayAddress = fullAddress || address;
      if (displayAddress) {
        setSelectedAddress(displayAddress);
        setLocation(displayAddress);
        console.log('✅ Updated location to:', displayAddress);
      }
    };

    window.addEventListener('locationUpdated', handleLocationUpdate);
    return () => {
      window.removeEventListener('locationUpdated', handleLocationUpdate);
    };
  }, []);

  // Fetch notification count
  const fetchNotificationCount = async () => {
    try {
      const notifications = await notificationService.fetchNotifications();
      const unreadCount = notifications.filter(n => !n.isRead).length;
      setNotificationCount(unreadCount);
    } catch (error) {
      console.error('Error fetching notification count:', error);
      setNotificationCount(0);
    }
  };

  // Fetch notification and chat counts on component mount and when user changes
  useEffect(() => {
    if (!isGuest) {
      fetchNotificationCount();
      refreshUnreadCount();
    }
  }, [isGuest, refreshUnreadCount]);

  // Refresh notification and chat counts when user returns to the page
  useEffect(() => {
    const handleFocus = () => {
      if (!isGuest) {
        fetchNotificationCount();
        refreshUnreadCount();
      }
    };

    // Check if user just returned from notifications page
    const checkNotificationsViewed = () => {
      const notificationsViewed = localStorage.getItem('notificationsViewed');
      if (notificationsViewed === 'true') {
        setNotificationCount(0);
        localStorage.removeItem('notificationsViewed');
      }
    };

    window.addEventListener('focus', handleFocus);
    checkNotificationsViewed();
    
    return () => window.removeEventListener('focus', handleFocus);
  }, [isGuest, refreshUnreadCount]);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');

  const [notificationCount, setNotificationCount] = useState(0);
  const [searchError, setSearchError] = useState('');
  
  // Guest mode states
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [restrictedFeature, setRestrictedFeature] = useState('');
  
  // Filter states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [serviceModes, setServiceModes] = useState(['In Salon', 'At Home']);
  const [providerTypes, setProviderTypes] = useState(['salon', 'pros']);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [distanceType, setDistanceType] = useState('distance'); // 'entire-city' or 'distance'
  const [distance, setDistance] = useState(50);
  const [gender, setGender] = useState('Unisex');
  const [rating, setRating] = useState(0);
  const [sortBy, setSortBy] = useState('distance');
  
  // Hygiene cards auto-scroll
  const hygieneScrollRef = React.useRef(null);
  const [isHygieneScrollPaused, setIsHygieneScrollPaused] = useState(false);

  // Reset filters function
  const resetFilters = () => {
    setServiceModes(['In Salon', 'At Home']);
    setProviderTypes(['salon', 'pros']);
    setSelectedCategories([]);
    setDistanceType('distance');
    setDistance(50);
    setGender('Unisex');
    setRating(0);
    setSortBy('distance');
  };
  
  // Auto-scroll for Hygiene & Safety cards
  useEffect(() => {
    if (!hygieneScrollRef.current || isHygieneScrollPaused) return;
    
    const scrollContainer = hygieneScrollRef.current;
    const cardWidth = 200 + 16; // card minWidth + gap
    let scrollPosition = 0;
    
    const autoScroll = setInterval(() => {
      if (scrollContainer) {
        scrollPosition += cardWidth;
        
        // If reached the end, smoothly scroll back to start
        if (scrollPosition >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
          scrollPosition = 0;
        }
        
        scrollContainer.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 3000); // Scroll every 3 seconds
    
    return () => clearInterval(autoScroll);
  }, [isHygieneScrollPaused]);


  const [featuredServices, setFeaturedServices] = useState([]);
  const [featuredServicesLoading, setFeaturedServicesLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState({
    all: [],
    today: [],
    week: [],
    month: [],
    featured: []
  });

  // Add CSS for banner carousel animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes bannerFadeInOut {
        0%, 25% { opacity: 1; }
        33%, 100% { opacity: 0; }
      }
      
      @keyframes fadeInOut {
        0%, 25% { opacity: 1; }
        33%, 100% { opacity: 0; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch all home data from backend using single API call
  const fetchAllHomeData = async (options = {}) => {
    const background = options.background === true;
    try {
      if (!background) {
        setBannersLoading(true);
        setCategoriesLoading(true);
        setProvidersLoading(true);
        setFeaturedServicesLoading(true);
      }

      // Sync country from profile/timezone — never block on IP detection
      ensureSyncCountryCode();

      // Get user location and address for providers
      const savedLocation = localStorage.getItem('userLocation');
      const savedAddress = localStorage.getItem('userAddress');
      let locationParams = {};
      if (savedLocation) {
        try {
          const locationData = JSON.parse(savedLocation);
          if (locationData.latitude && locationData.longitude) {
            locationParams = {
              latitude: locationData.latitude,
              longitude: locationData.longitude,
              address: savedAddress // Include address for city filtering
            };
          } else {
          }
        } catch (error) {
          console.error('Error parsing saved location:', error);
        }
      } else {
      }
      
      // Fetch all data using single API call
      const response = await homeAPI.getAllData(locationParams);
        
        
        // Products are now included in the all-data response
        
        // Set banners
        if (response.data && response.data.banners) {
          setBanners(response.data.banners);
        } else if (response.banners) {
          setBanners(response.banners);
        } else {
          setBanners([]);
        }
        
        // Set categories (exclude Offers / Deals / Packages from Home only)
        if (response.data && response.data.categories) {
          setCategories(filterCategoriesForHomeDisplay(response.data.categories));
        } else if (response.categories) {
          setCategories(filterCategoriesForHomeDisplay(response.categories));
        } else {
          setCategories([]);
        }
        
        // Set providers (salons and pros) — same host as axios `api` (REACT_APP_API_URL), not hardcoded prod
        setProviders(extractProvidersFromHomeAllDataResponse(response));
        
        // Set products from all-data response
        if (response.data && response.data.products) {
          setProducts(response.data.products);
        } else if (response.products) {
          setProducts(response.products);
        } else {
          setProducts([]);
        }
        
        // Set featured services
        if (response.data && response.data.services) {
          setFeaturedServices(response.data.services);
        } else if (response.services) {
          setFeaturedServices(response.services);
        } else {
          setFeaturedServices([]);
        }
        
        // Set offers - DISABLED: Special offers section is hidden
        setOffers({
          all: [],
          today: [],
          week: [],
          month: [],
          featured: []
        });
        
      setBannersLoading(false);
      setCategoriesLoading(false);
      setProvidersLoading(false);
      setFeaturedServicesLoading(false);
      
    } catch (error) {
      console.error('Error fetching all home data:', error);
      
      // Set empty arrays when API fails - no fallback data
      setBanners([]);
      setCategories([]);
      setProviders([]);
      setProducts([]);
      setFeaturedServices([]);
      // DISABLED: Special offers section is hidden
      setOffers({
        all: [],
        today: [],
        week: [],
        month: [],
        featured: []
      });
      
      setBannersLoading(false);
      setCategoriesLoading(false);
      setProvidersLoading(false);
      setFeaturedServicesLoading(false);
    }
  };

  useEffect(() => {
    fetchAllHomeData();

    const handleCountryChanged = () => {
      fetchAllHomeData({ background: true });
    };
    window.addEventListener('app:country-changed', handleCountryChanged);
    return () => window.removeEventListener('app:country-changed', handleCountryChanged);
  }, []);





  // Guest mode handlers
  const handleGuestRestriction = (featureName) => {
    setRestrictedFeature(featureName);
    setShowSignUpPrompt(true);
  };

  // Cart handling functions
  const handleAddToCart = (item, itemType = 'product', providerId = 'home') => {
    if (isGuest) {
      handleGuestRestriction('add to cart');
      return;
    }
    addToCart(item, itemType, providerId);
  };

  const handleQuantityChange = (itemId, newQuantity, itemType = 'product', providerId = 'home') => {
    if (isGuest) {
      handleGuestRestriction('update cart');
      return;
    }
    
    // Find the cart item to update
    const cartItem = items.find(item => {
      if (itemType === 'service') {
        return item.id === itemId && item.itemType === 'service' && item.providerId === providerId;
      } else {
        return item.id === itemId && item.itemType === 'product';
      }
    });
    
    if (cartItem) {
      updateItemQuantity(cartItem, newQuantity);
    }
  };

  // Gender icon function
  const getGenderIcon = (gender) => {
    if (!gender || gender.trim() === '') {
      return null;
    }
    
    switch (gender) {
      case 'Male':
        return <Male sx={{ fontSize: 16, color: '#1976d2' }} />;
      case 'Female':
        return <Female sx={{ fontSize: 16, color: '#e91e63' }} />;
      case 'Both':
      case 'Unisex':
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Male sx={{ fontSize: 14, color: '#1976d2' }} />
            <Female sx={{ fontSize: 14, color: '#e91e63' }} />
          </Box>
        );
      default:
        return null;
    }
  };

  // Add to cart for services and offers (no navigation)
  const handleServiceAddToCart = (service) => {
    if (isGuest) {
      handleGuestRestriction('book services');
      return;
    }
    
    // Ensure service has a proper ID and all required fields
    const serviceWithId = {
      ...service,
      id: service.id || service._id,
      name: service.name || service.serviceName,
      price: service.price || service.customerPrice,
      image: service.image || service.displayImage || '/placeholder-service.jpg',
      duration: service.duration,
      providerName: service.providerName || service.provider?.businessName || 'Provider',
      itemType: 'service'
    };
    
    // Debug: Log service image data
  //  console.log('Home - Service image data:', {
  //    name: service.serviceName || service.name,
 //     image: service.image,
  //    displayImage: service.displayImage,
  //    finalImage: service.image || service.displayImage || '/placeholder-service.jpg'
  //  });
    
    // Use actual provider ID for proper separation
    const providerId = service.providerId || service.provider?._id || 'Unknown Provider';
    
    addToCart(serviceWithId);
  };

  const handleOfferAddToCart = (offer) => {
    if (isGuest) {
      handleGuestRestriction('book services');
      return;
    }
    
    // Ensure offer has a proper ID
    const offerWithId = {
      ...offer,
      id: offer.id || offer._id || `offer-${offer.serviceName}-${offer.providerName}`
    };
    
    // Use provider name as providerId for proper separation
    const providerId = offer.providerName || 'Unknown Provider';
    addToCart(offerWithId, 'offer', providerId);
  };

  const handleProductAddToCart = (product) => {
    if (isGuest) {
      handleGuestRestriction('book services');
      return;
    }
    
    // Ensure product has a proper ID
    const productWithId = {
      ...product,
      id: product.id || product._id || `product-${product.productName}`
    };
    
    // Use 'home' as providerId for products
    addToCart(productWithId, 'product', 'home');
  };

  // Helper function to get provider ID for cart operations
  const getProviderId = (item) => {
    return item.providerName || 'Unknown Provider';
  };


  // Location functions
  const getCurrentLocation = async () => {
    try {
      setLocationLoading(true);
      setLocationError('');
      
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by this browser');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Get address from coordinates using reverse geocoding
          try {
            // Get auth token
            const customerToken = localStorage.getItem('customerToken');
            const token = localStorage.getItem('token');
            const authToken = customerToken || token;
            
            const headers = { 'Content-Type': 'application/json' };
            if (authToken) {
              headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            // PRODUCTION: Already using production URL, but ensuring consistency
            // const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/locations/reverse-geocode`, { // COMMENTED - Already production
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/locations/reverse-geocode`, {
              method: 'POST',
              headers: headers,
              body: JSON.stringify({ lat: latitude, lng: longitude })
            });
            const data = await response.json();
            
            if (data.success && data.data) {
              const fullAddress = data.data.formatted_address;
              
              // Save full address to localStorage
              const locationData = {
                address: fullAddress,
                fullAddress: fullAddress,
                latitude,
                longitude,
                city: fullAddress ? fullAddress.split(',')[0] : '', // Extract city from address
                type: 'current'
              };
              localStorage.setItem('userLocation', JSON.stringify(locationData));
              localStorage.setItem('userAddress', fullAddress);
              
              // Update state with full address
              setSelectedAddress(fullAddress);
              setLocation(fullAddress);
              // console.log('Current location saved to localStorage:', locationData);
              
            // Refresh data with new location
           // console.log('📍 GPS location updated, refreshing data with new coordinates...');
            fetchAllHomeData();
            }
          } catch (error) {
            console.error('Reverse geocoding failed:', error);
            setSelectedAddress('Current Location (GPS)');
            setLocation('Current Location (GPS)');
            
            // Save basic location data
            const locationData = {
              address: 'Current Location (GPS)',
              fullAddress: 'Current Location (GPS)',
              latitude,
              longitude,
              type: 'current'
            };
            localStorage.setItem('userLocation', JSON.stringify(locationData));
            localStorage.setItem('userAddress', 'Current Location (GPS)');
            
            // Refresh data with new location
          //  console.log('📍 GPS location updated (fallback), refreshing data with new coordinates...');
            fetchAllHomeData();
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationError('Failed to get current location. Please search for your address.');
        }
      );
    } catch (error) {
      setLocationError('Failed to get current location');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSearchLocation = async () => {
    if (!locationSearchQuery.trim()) return;

    try {
      setLocationLoading(true);
      // This would typically call a geocoding service
      // For now, we'll simulate with mock data
      const mockSuggestions = [
        { address: `${locationSearchQuery}, New York, NY`, latitude: 40.7128, longitude: -74.0060 },
        { address: `${locationSearchQuery}, Los Angeles, CA`, latitude: 34.0522, longitude: -118.2437 },
        { address: `${locationSearchQuery}, Chicago, IL`, latitude: 41.8781, longitude: -87.6298 },
        { address: `${locationSearchQuery}, Miami, FL`, latitude: 25.7617, longitude: -80.1918 },
        { address: `${locationSearchQuery}, Seattle, WA`, latitude: 47.6062, longitude: -122.3321 },
      ];
      setLocationSuggestions(mockSuggestions);
    } catch (error) {
      setLocationError('Failed to search for location');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSelectLocation = (suggestion) => {
    setSelectedAddress(suggestion.address);
    setLocationSuggestions([]);
    setLocationSearchQuery(suggestion.address);
    
    // Save selected location to localStorage
    const locationData = {
      address: suggestion.address,
      fullAddress: suggestion.address,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
      type: 'selected'
    };
    localStorage.setItem('userLocation', JSON.stringify(locationData));
    localStorage.setItem('userAddress', suggestion.address);
    
    // Refresh data with new location
    fetchAllHomeData();
  };

  const handleBookNow = (banner) => {
    if (banner.buttonText === 'Book Now') {
      if (isGuest) {
        handleGuestRestriction('booking services');
        return;
      }
      
      // Create a mock provider and cart items for the banner
      const provider = {
        id: 'banner-provider',
        name: banner.title,
        image: banner.image,
        rating: 4.8,
        distance: '0.5 km'
      };
      
      const cartItems = [
        {
          id: 'banner-service',
          name: banner.title,
          price: 50,
          duration: 60,
          image: banner.image
        }
      ];
      
      // Navigate to availability screen with providerId in URL
      navigate('/availability/banner-provider', {
        state: {
          provider,
          cartItems
        }
      });
    } else if (banner.buttonText === 'Explore') {
      // Navigate to explore page or show more options
      navigate('/providers');
    }
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5' }}>
             {/* Header */}
       <AppBar position="static" sx={{ backgroundColor: '#1a237e', color: '#ffffff' }}>
         <Toolbar sx={{ 
           minHeight: { xs: '56px', sm: '64px' },
           px: { xs: 1, sm: 2, md: 3 }
         }}>
           {/* Logo */}
           <Box sx={{ 
             flexGrow: 0, 
             mr: { xs: 1, sm: 2, md: 3 },
             display: 'flex',
             alignItems: 'center'
           }}>
             <img 
               src="/logo.png" 
               alt="NISTYLEZ Logo"
               style={{ 
                 width: isMobile ? '100px' : '140px', 
                 height: isMobile ? '35px' : '50px',
                 borderRadius: '8px',
                 objectFit: 'contain'
               }}
             />
           </Box>

                     {/* Search Bar with Filters */}
           <Box sx={{ 
             display: isMobile ? 'none' : 'flex', 
             alignItems: 'center', 
             flexGrow: 1, 
             mr: { xs: 1, sm: 2 }, 
             ml: { xs: 1, sm: 2 },
             minWidth: 0
           }}>
             <Search sx={{ 
               flexGrow: 1, 
               mr: 1, 
               maxWidth: { xs: 200, sm: 300, md: 400 }
             }}>
               <StyledInputBase
                 placeholder="Search for services..."
                 inputProps={{ 'aria-label': 'search' }}
                 value={searchQuery}
                 onChange={(e) => {
                   setSearchQuery(e.target.value);
                   // Clear error when user starts typing
                   if (searchError) {
                     setSearchError('');
                   }
                 }}
                 onKeyPress={(e) => {
                     if (e.key === 'Enter') {
                       if (searchQuery.trim()) {
                         setSearchError('');
                                         navigate(`/search-results?q=${encodeURIComponent(searchQuery.trim())}`);
                       } else {
                         // Show error for empty search
                         setSearchError('Please enter a search term');
                       }
                   }
                   }}
                 sx={{
                   '& .MuiInputBase-input': {
                     padding: theme.spacing(1, 1, 1, 2), // Remove left padding since no icon
                     fontSize: { xs: '0.875rem', sm: '1rem' }
                   },
                 }}
               />
               <IconButton
                 onClick={() => setFilterDialogOpen(true)}
                 sx={{ 
                   position: 'absolute',
                   right: 40,
                   color: 'rgba(255,255,255,0.7)',
                   '&:hover': { color: 'white' },
                   fontSize: { xs: '18px', sm: '24px' }
                 }}
               >
                 <FilterListIcon />
               </IconButton>
               <IconButton
                 onClick={() => {
                   if (searchQuery.trim()) {
                     setSearchError('');
                                         navigate(`/search-results?q=${encodeURIComponent(searchQuery.trim())}`);
                   } else {
                     // Show error for empty search
                     setSearchError('Please enter a search term');
                   }
                 }}
                 sx={{ 
                   position: 'absolute',
                   right: 8,
                   color: 'rgba(255,255,255,0.7)',
                   '&:hover': { color: 'white' },
                   fontSize: { xs: '18px', sm: '24px' }
                 }}
               >
                 <SearchIcon />
               </IconButton>
             </Search>
             {searchError && (
               <Box sx={{ mt: 1, ml: 2 }}>
                 <Typography variant="body2" sx={{ color: '#ff6b6b', fontSize: '0.875rem' }}>
                   {searchError}
                 </Typography>
               </Box>
             )}
           </Box>

           {/* Mobile Search Button - Show only on mobile */}
           <Box sx={{ display: { xs: 'block', md: 'none' } }}>
             <IconButton
               color="inherit"
               onClick={() => {
                 // For mobile, we could show a search dialog or navigate to search page
                 navigate('/search-results');
               }}
               sx={{ 
                 p: 0.5,
                 minWidth: '40px',
                 minHeight: '40px',
                 mr: 1
               }}
             >
               <SearchIcon sx={{ fontSize: '20px' }} />
             </IconButton>
           </Box>

                     {/* Location Bar */}
           <Box sx={{ 
             display: 'flex', 
             alignItems: 'center', 
             mr: { xs: 1, sm: 2 },
             minWidth: 0
          }}>
            <LocationIcon sx={{ 
              mr: { xs: 0.5, sm: 1 }, 
              color: 'white',
              fontSize: { xs: '18px', sm: '24px' }
            }} />
            <Box sx={{
               display: 'flex', 
               alignItems: 'center', 
               cursor: 'pointer',
               minWidth: 0
             }} onClick={() => setLocationDialogOpen(true)}>
               <Typography variant="body2" sx={{ 
                 color: 'white', 
                 mr: 0.5, 
                 maxWidth: { xs: 120, sm: 200, md: 300 }, 
                 overflow: 'hidden', 
                 textOverflow: 'ellipsis', 
                 whiteSpace: 'nowrap',
                 fontSize: { xs: '0.75rem', sm: '0.875rem' }
               }}>
                 {selectedAddress || location || 'Current Location'}
               </Typography>
               <ArrowDownIcon sx={{ 
                 color: 'white', 
                 fontSize: { xs: 14, sm: 16 }
               }} />
             </Box>
           </Box>


                     {/* Right side icons */}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <IconButton 
              color="inherit" 
              sx={{ 
                mr: { xs: 0, sm: 1 },
                p: { xs: 0.5, sm: 1 },
                minWidth: { xs: '40px', sm: 'auto' },
                minHeight: { xs: '40px', sm: 'auto' }
              }}
              onClick={() => {
                if (isGuest) {
                  handleGuestRestriction('chat');
                } else {
                  navigate('/chat');
                }
              }}
            >
              <Badge badgeContent={chatCount > 0 ? chatCount : null} color="error" max={99}>
                <ChatIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
              </Badge>
            </IconButton>
            <IconButton 
              color="inherit" 
              sx={{ 
                mr: { xs: 0, sm: 1 },
                p: { xs: 0.5, sm: 1 },
                minWidth: { xs: '40px', sm: 'auto' },
                minHeight: { xs: '40px', sm: 'auto' }
              }}
              onClick={() => {
                if (isGuest) {
                  handleGuestRestriction('notifications');
                } else {
                  // Reset notification count when user opens notifications page
                  setNotificationCount(0);
                  navigate('/notifications');
                }
              }}
            >
              <Badge 
                badgeContent={notificationCount > 0 ? notificationCount : null} 
                color="error"
              >
                <NotificationsIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
              </Badge>
            </IconButton>
            <IconButton 
              color="inherit"
              sx={{ 
                p: { xs: 0.5, sm: 1 },
                minWidth: { xs: '40px', sm: 'auto' },
                minHeight: { xs: '40px', sm: 'auto' }
              }}
              onClick={() => {
                // Dispatch custom event to trigger cart dialog
                window.dispatchEvent(new CustomEvent('openCart'));
              }}
            >
              <Badge badgeContent={totalItems} color="error">
                <CartIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
              </Badge>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

              {/* Main Content */}
        <Box sx={{ 
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: { xs: 1, sm: 2 },
          pb: { xs: 10, sm: 3 } // Add bottom padding for mobile to account for bottom navigation
        }}>
                    {/* Auto-Changing Banner Carousel - Real Banners Only */}
          <Box sx={{ mb: { xs: 0.5, sm: 1 }, maxWidth: '1472px', mx: 'auto' }}>
             <Box sx={{ 
               position: 'relative', 
               height: { xs: 300, sm: 350, md: 400 }, 
               overflow: 'hidden',
               backgroundColor: 'transparent'
             }}>
               {banners.length > 0 ? (
                 (() => {
                  // console.log('🎯 About to render banners, count:', banners.length);
                   return banners.map((banner, index) => {
                  //   console.log(`🎨 Rendering banner ${index + 1}:`, {
                    //   id: banner.id,
                   //    title: banner.title,
                   //    gradient: banner.gradient,
                   //    animation: `bannerFadeInOut ${banners.length * 3}s infinite`,
                    //   animationDelay: `${index * 3}s`
                   //  });
                     return (
                      <Box
                        key={banner._id || banner.id}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: `url(${banner.imageUrl || banner.image})`,
                          backgroundSize: 'contain',
                          backgroundPosition: 'center center',
                          backgroundRepeat: 'no-repeat',
                          backgroundColor: '#f5f5f5',
                          opacity: 0,
                          transition: 'opacity 0.8s ease-in-out',
                          zIndex: 1,
                          animation: `bannerFadeInOut ${banners.length * 5}s infinite`,
                          animationDelay: `${index * 5}s`
                        }}
                      />
                     );
                   });
                 })()
               ) : (
                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                   <Typography variant="h6" color="text.secondary">
                     No banners available
                   </Typography>
                 </Box>
               )}
             </Box>
           </Box>

                    {/* ======== OLD CATEGORY DESIGN (Circular Icons) - COMMENTED FOR ROLLBACK ======== */}
          {/* <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Categories
            </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => navigate('/categories?tab=salon')}
                sx={{ color: '#1976d2', borderColor: '#1976d2', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                View All
              </Button>
            </Box>
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: 1, sm: 2 },
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              scrollSnapType: 'x mandatory',
              pb: 2,
              px: { xs: 1, sm: 0 },
              '& > *': {
                flexShrink: 0,
                scrollSnapAlign: 'start'
              }
            }}>
              {categories.length > 0 ? (
                categories.map((category, index) => (
                 <Box key={index} sx={{ 
                   textAlign: 'center', 
                   cursor: 'pointer',
                   transition: 'transform 0.2s',
                   flexShrink: 0,
                   minWidth: { xs: 85, sm: 80 },
                   '&:hover': { transform: 'translateY(-4px)' }
                 }}
                 onClick={() => navigate(`/categories?category=${encodeURIComponent(category.name)}&tab=salon`)}
                 >
                   <Box sx={{ 
                     width: { xs: 70, sm: 60 }, 
                     height: { xs: 70, sm: 60 }, 
                     borderRadius: '50%',
                     background: `linear-gradient(135deg, ${category.color} 0%, ${category.color}dd 100%)`,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     margin: '0 auto 8px',
                     boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                     border: '2px solid white',
                     position: 'relative',
                     '&::before': {
                       content: '""',
                       position: 'absolute',
                       top: '-2px',
                       left: '-2px',
                       right: '-2px',
                       bottom: '-2px',
                       borderRadius: '50%',
                       background: `linear-gradient(45deg, ${category.color}, ${category.color}88)`,
                       zIndex: -1
                     }
                   }}>
                     {category.iconUrl ? (
                       <img 
                         src={category.iconUrl} 
                         alt={category.name}
                         style={{ 
                           width: isMobile ? '38px' : '32px', 
                           height: isMobile ? '38px' : '32px',
                           objectFit: 'contain'
                         }}
                       />
                     ) : (
                       <Typography variant="h4" sx={{ fontSize: { xs: '2.2rem', sm: '1.8rem' } }}>
                         {category.icon || '✨'}
                       </Typography>
                     )}
                   </Box>
                   <Typography variant="caption" sx={{ 
                     fontWeight: 'bold', 
                     color: '#333',
                     fontSize: { xs: '0.85rem', sm: '0.7rem' },
                     display: 'block',
                     textAlign: 'center'
                   }}>
                     {category.name}
                   </Typography>
                 </Box>
                ))
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No categories available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box> */}

          {/* ======== NEW CATEGORY DESIGN (Figma Style - Card Based) ======== */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 1.5 } }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Categories
              </Typography>
              <Button 
                variant="contained" 
                size="small" 
                onClick={() => navigate('/categories?tab=salon')}
                sx={{ 
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: 'white',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  fontWeight: 600,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 0.6 },
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.35)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                View All
              </Button>
            </Box>
            
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: 2, sm: 2.5 },
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              pb: 2,
              pt: { xs: 1, sm: 1 },
              px: { xs: 1, sm: 0 },
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 4 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 4 },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' }
            }}>
              {categoriesLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : categories.length > 0 ? (
                categories.map((category, index) => (
                  <Card 
                    key={index}
                    onClick={() => navigate(`/categories?category=${encodeURIComponent(category.name)}&tab=salon`)}
                    sx={{ 
                      minWidth: { xs: 120, sm: 130, md: 140 },
                      maxWidth: { xs: 120, sm: 130, md: 140 },
                      height: { xs: 130, sm: 140, md: 150 },
                      background: 'linear-gradient(180deg, rgba(230, 242, 255, 0.4) 0%, rgba(200, 228, 255, 0.6) 18%, #2874BA 100%)',
                      borderRadius: { xs: '0 30px 0 0', sm: '0 40px 0 0', md: '0 45px 0 0' },
                      padding: { xs: '8px 8px 14px', sm: '10px 10px 16px', md: '12px 12px 20px' },
                      position: 'relative',
                      boxShadow: '0 3px 12px rgba(40, 116, 186, 0.18)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 10px 30px rgba(40, 116, 186, 0.35)'
                      }
                    }}
                  >
                    {/* Icon at top - INSIDE card */}
                    <Box sx={{ 
                      position: 'absolute',
                      top: { xs: 8, sm: 10, md: 12 },
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: { xs: 52, sm: 58, md: 64 },
                      height: { xs: 52, sm: 58, md: 64 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
                      zIndex: 10
                    }}>
                      {category.iconUrl ? (
                        <img 
                          src={category.iconUrl} 
                          alt={category.name}
                          style={{ 
                            width: isMobile ? '48px' : '56px', 
                            height: isMobile ? '48px' : '56px',
                            objectFit: 'contain'
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: { xs: '2.4rem', sm: '2.7rem', md: '3rem' } }}>
                          {category.icon || '✨'}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Category Name in Middle */}
                    <Typography variant="body2" sx={{ 
                      fontWeight: 700,
                      color: 'white',
                      fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                      textAlign: 'center',
                      marginTop: 'auto',
                      marginBottom: 'auto',
                      paddingTop: { xs: '50px', sm: '55px', md: '60px' },
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.15)'
                    }}>
                      {category.name}
                    </Typography>
                  </Card>
                ))
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No categories available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          {/* ======== END NEW CATEGORY DESIGN ======== */}

                     {/* Recommended for You - Salon and Pros Section */}
           <Box sx={{ mb: 4 }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
               <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                 Recommended for You
               </Typography>
             </Box>
            
                         {/* Salon Section - Enhanced Cards with Photo Gallery */}
             <Box sx={{ mb: 3 }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#666', fontWeight: 'medium' }}>
                  🏪 Salons Near You
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={() => navigate('/categories?tab=salon')}
                  sx={{ 
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: 'white',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    fontWeight: 600,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 0.6 },
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.35)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  View All
                </Button>
              </Box>
               <Box sx={{ 
                 display: 'flex', 
                 gap: 2, 
                 overflowX: 'auto', 
                 pb: 2,
                 '&::-webkit-scrollbar': { height: 8 },
                 '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' }
               }}>
                 {providersLoading ? (
                   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 4 }}>
                     <CircularProgress size={40} />
                   </Box>
                 ) : (() => {
                   const salonProviders = providers.filter(
                     (provider) => getHomeProviderKind(provider) === 'salon'
                   );
                   
                   return salonProviders.length > 0 ? (
                     // Real data from backend
                     salonProviders.map((salon, index) => (
                       <Card key={index} sx={{ 
                         minWidth: 240, 
                         height: 'auto',
                         minHeight: 320,
                         cursor: 'pointer', 
                         borderRadius: 3,
                         overflow: 'hidden',
                         boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                         transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                         flexShrink: 0,
                         display: 'flex',
                         flexDirection: 'column',
                         '&:hover': { 
                           transform: 'translateY(-8px)',
                           boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                         }
                       }}
                       onClick={() => navigate(`/provider/${salon._id}`, {
                         state: { distance: salon.distance }
                       })}>
                         {/* Photo Gallery with Auto Scroll */}
                         <Box sx={{ position: 'relative', height: 180, flexShrink: 0 }}>
                           {/* Use photoGallery from backend data for auto-scrolling */}
                           {salon.photoGallery && salon.photoGallery.length > 1 ? (
                             // Multiple images - show auto-scrolling gallery
                             salon.photoGallery.map((image, imgIndex) => (
                               <Box
                                 key={imgIndex}
                                 sx={{
                                   position: 'absolute',
                                   top: 0,
                                   left: 0,
                                   right: 0,
                                   bottom: 0,
                                   opacity: imgIndex === 0 ? 1 : 0,
                                   transition: 'opacity 0.5s ease-in-out',
                                   animation: `fadeInOut ${salon.photoGallery.length * 2}s infinite`,
                                   animationDelay: `${imgIndex * 2}s`,
                                 }}
                               >
                                 <img 
                                   src={image || '/placeholder-salon.jpg'} 
                                   alt={`${salon.businessName} ${imgIndex + 1}`}
                                   style={{ 
                                     width: '100%', 
                                     height: '100%', 
                                     objectFit: 'cover' 
                                   }}
                                   onError={(e) => {
                                     e.target.src = '/placeholder-salon.jpg';
                                   }}
                                 />
                               </Box>
                             ))
                           ) : (
                             // Single image or fallback
                             <img 
                               src={salon.photoGallery?.[0] || salon.businessImage || '/placeholder-salon.jpg'} 
                               alt={salon.businessName}
                               style={{ 
                                 width: '100%', 
                                 height: '100%', 
                                 objectFit: 'cover' 
                               }}
                               onError={(e) => {
                                 e.target.src = '/placeholder-salon.jpg';
                               }}
                             />
                           )}
                           {/* Status Badges */}
                           <Box sx={{ 
                             position: 'absolute', 
                             top: 8, 
                             left: 8, 
                             display: 'flex', 
                             gap: 1 
                           }}>
                             <Chip 
                               label="Open" 
                               size="small" 
                               sx={{ 
                                 backgroundColor: '#4caf50', 
                                 color: 'white',
                                 fontSize: '0.5rem',
                                 height: 18
                               }}
                             />
                           </Box>
                           {/* Verified Badge - Top Right */}
                           <Box sx={{ 
                             position: 'absolute', 
                             top: 8, 
                             right: 8 
                           }}>
                             <Box sx={{ 
                               backgroundColor: '#1976d2', 
                               borderRadius: '50%',
                               width: 24,
                               height: 24,
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                             }}>
                               <CheckCircle sx={{ color: 'white', fontSize: 16 }} />
                             </Box>
                           </Box>
                         </Box>
                         <CardContent sx={{ py: 1, px: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                           <Box sx={{ mb: 0.5 }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 'bold', 
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                  color: '#1976d2',
                                  textDecoration: 'none',
                                  mb: 0.5,
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    color: '#1565c0'
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (salon._id) {
                                    navigate(`/provider/${salon._id}`, {
                                      state: { distance: salon.distance }
                                    });
                                  } else {
                                    console.error('Salon ID is missing:', salon);
                                  }
                                }}
                              >
                               {salon.businessName}
                             </Typography>
                             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <StarIcon sx={{ color: '#ffd700', fontSize: 14, mr: 0.5 }} />
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                                  {(() => {
                                    const hasRating = salon.averageRating || salon.rating;
                                    const reviewCount = salon.reviewCount || salon.totalReviews || 0;
                                    // Show actual rating only if there are reviews and rating exists, otherwise show 5 stars
                                    const rating = (reviewCount > 0 && hasRating) ? hasRating : 5;
                                    return typeof rating === 'number' ? rating.toFixed(1) : rating;
                                  })()}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <LocationIcon sx={{ color: '#1976d2', fontSize: 14, mr: 0.5 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {salon.distance || 'Distance unavailable'}
                                </Typography>
                              </Box>
                            </Box>
                           </Box>
                           <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                             <Chip 
                               label="In Salon" 
                               size="small" 
                               sx={{ 
                                 backgroundColor: '#e3f2fd',
                                 fontSize: '0.6rem',
                                 fontWeight: 'bold',
                                 height: 20
                               }}
                             />
                           </Box>
                           {/* Removed duplicate salon chip - provider type is already clear from context */}
                         </CardContent>
                       </Card>
                     ))
                   ) : (
                     // No data state
                     <Box sx={{ 
                       display: 'flex', 
                       alignItems: 'center', 
                       justifyContent: 'center', 
                       width: '100%', 
                       py: 4 
                     }}>
                       <Typography variant="body1" color="text.secondary">
                         No salons available at the moment
                       </Typography>
                     </Box>
                   );
                 })()}
                 </Box>
               </Box>

                         {/* Pros Section - Enhanced Cards with Photo Gallery */}
             <Box sx={{ mb: 3 }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#666', fontWeight: 'medium' }}>
                  👩‍🎨 Recommended Pros for You
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={() => navigate('/categories?tab=pros')}
                  sx={{ 
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: 'white',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    fontWeight: 600,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 0.6 },
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.35)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  View All
                </Button>
              </Box>
               <Box sx={{ 
                 display: 'flex', 
                 gap: 2, 
                 overflowX: 'auto', 
                 pb: 2,
                 '&::-webkit-scrollbar': { height: 8 },
                 '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' }
               }}>
                 {(() => {
                   const proProviders = providers.filter((provider) => {
                     const k = getHomeProviderKind(provider);
                     return k === 'pros' || k === 'pro';
                   });
                   
                   return proProviders.length > 0 ? (
                     proProviders.map((pro, index) => ({
                       id: pro._id || pro.id,
                       name: pro.businessName || pro.name || 'Professional',
                       rating: pro.averageRating || 4.5,
                       distance: pro.distance || null,
                       serviceModes: pro.serviceModes || ['At Home'],
                       images: pro.photoGallery || [pro.businessImage] || ['/placeholder-pro.jpg'],
                       services: pro.services || pro.businessSettings?.serviceCategories || ['Professional Services'],
                       verified: pro.verified || true,
                       available: pro.open || pro.available || true
                     })).map((pro, index) => (
                   <Card key={index} sx={{ 
                     minWidth: 240, 
                     cursor: 'pointer', 
                     borderRadius: 3,
                     overflow: 'hidden',
                     boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                     transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                     flexShrink: 0,
                     '&:hover': { 
                       transform: 'translateY(-8px)',
                       boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                     }
                   }}
                   onClick={() => navigate(`/provider/${pro.id}`, {
                     state: { selectedAddress, provider: pro }
                   })}>
                       {/* Photo Gallery with Auto Scroll */}
                       <Box sx={{ position: 'relative', height: 200 }}>
                         {pro.images.map((image, imgIndex) => (
                           <Box
                             key={imgIndex}
                             sx={{
                               position: 'absolute',
                               top: 0,
                               left: 0,
                               right: 0,
                               bottom: 0,
                               opacity: imgIndex === 0 ? 1 : 0,
                               transition: 'opacity 0.5s ease-in-out',
                               animation: `fadeInOut ${pro.images.length * 2}s infinite`,
                               animationDelay: `${imgIndex * 2}s`,
                             }}
                           >
                             <img 
                               src={image || '/placeholder-pro.jpg'} 
                               alt={`${pro.name} ${imgIndex + 1}`}
                               style={{ 
                                 width: '100%', 
                                 height: '100%', 
                                 objectFit: 'cover' 
                               }}
                               onError={(e) => {
                                 e.target.src = '/placeholder-pro.jpg';
                               }}
                             />
                           </Box>
                         ))}
                         {/* Status Badges */}
                         <Box sx={{ 
                           position: 'absolute', 
                           top: 8, 
                           left: 8, 
                           display: 'flex', 
                           gap: 1 
                         }}>
                           {pro.available && (
                             <Chip 
                               label="Available" 
                               size="small" 
                               sx={{ 
                                 backgroundColor: '#4caf50', 
                                 color: 'white',
                                 fontSize: '0.5rem',
                                 height: 18
                               }}
                             />
                           )}
                         </Box>
                         {/* Verified Badge - Top Right */}
                         {pro.verified && (
                           <Box sx={{ 
                             position: 'absolute', 
                             top: 8, 
                             right: 8 
                           }}>
                             <Box sx={{ 
                               backgroundColor: '#1976d2', 
                               borderRadius: '50%',
                               width: 24,
                               height: 24,
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                             }}>
                               <CheckCircle sx={{ color: 'white', fontSize: 16 }} />
                             </Box>
                           </Box>
                         )}
                       </Box>
                       <CardContent sx={{ py: 1, px: 2 }}>
                         <Box sx={{ mb: 0.5 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                                color: '#1976d2',
                                textDecoration: 'none',
                                mb: 0.5,
                                '&:hover': {
                                  textDecoration: 'underline',
                                  color: '#1565c0'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (pro.id) {
                                  navigate(`/provider/${pro.id}`, {
                                    state: { selectedAddress, provider: pro }
                                  });
                                } else {
                                  console.error('Professional ID is missing:', pro);
                                }
                              }}
                            >
                             {pro.name}
                           </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <StarIcon sx={{ color: '#ffd700', fontSize: 14, mr: 0.5 }} />
                              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                                {(() => {
                                  const hasRating = pro.rating;
                                  const reviewCount = pro.reviewCount || pro.totalReviews || 0;
                                  // Show actual rating only if there are reviews and rating exists, otherwise show 5 stars
                                  const rating = (reviewCount > 0 && hasRating) ? hasRating : 5;
                                  return typeof rating === 'number' ? rating.toFixed(1) : rating;
                                })()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LocationIcon sx={{ color: '#1976d2', fontSize: 14, mr: 0.5 }} />
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {pro.distance || 'Distance unavailable'}
                              </Typography>
                            </Box>
                          </Box>
                         </Box>
                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                           {pro.serviceModes.map((mode, idx) => (
                             <Chip 
                               key={idx} 
                               label={mode} 
                               size="small" 
                               sx={{ 
                                 backgroundColor: mode === 'In Salon' ? '#e3f2fd' : '#f3e5f5',
                                 fontSize: '0.6rem',
                                 fontWeight: 'bold',
                                 height: 20
                               }}
                             />
                           ))}
                         </Box>
                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                           {pro.services.map((service, idx) => (
                             <Chip 
                               key={idx} 
                               label={service} 
                               size="small" 
                               variant="outlined"
                               sx={{ 
                                 fontSize: '0.6rem',
                                 borderColor: '#ddd',
                                 color: '#666',
                                 height: 18
                               }}
                             />
                           ))}
                         </Box>
                       </CardContent>
                     </Card>
                   ))
                 ) : (
                   // No pros available
                   <Box sx={{ 
                     display: 'flex', 
                     alignItems: 'center', 
                     justifyContent: 'center', 
                     width: '100%', 
                     py: 4 
                   }}>
                     <Typography variant="body1" color="text.secondary">
                       No pros available at the moment
                     </Typography>
                   </Box>
                 );
               })()}
                 </Box>
               </Box>
             </Box>

                     {/* Featured Services Section - Horizontal Scrollable */}
           <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                Featured Services
              </Typography>
              <Button 
                variant="contained" 
                size="small" 
                onClick={() => {
                  navigate('/featured-services');
                }}
                sx={{ 
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: 'white',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  fontWeight: 600,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 0.6 },
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.35)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                View All
              </Button>
            </Box>
             <Box sx={{ 
               display: 'flex',
               flexDirection: 'row',
               gap: 2,
               overflowX: 'auto',
               overflowY: 'hidden',
               WebkitOverflowScrolling: 'touch',
               scrollSnapType: 'x mandatory',
               pb: 2,
               '& > *': {
                 flexShrink: 0,
                 scrollSnapAlign: 'start'
               }
             }}>
               {featuredServicesLoading ? (
                 <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 4 }}>
                   <BrandLoader />
                 </Box>
               ) : featuredServices.length > 0 ? (
                 featuredServices.map((service, index) => (
                   <Card key={index} 
                     onClick={() => navigate(`/service/${service._id || service.id || `service-${index + 1}`}/${service.providerId || ''}`)}
                     sx={{ 
                       minWidth: { xs: 200, sm: 240 }, 
                       maxWidth: { xs: 200, sm: 240 },
                       cursor: 'pointer', 
                       borderRadius: 3,
                       flexShrink: 0,
                       boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                       transition: 'all 0.3s ease',
                       '&:hover': {
                         transform: 'translateY(-4px)',
                         boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                       }
                     }}
                   >
                     <CardMedia
                       component="img"
                       height={{ xs: 120, sm: 140, md: 160 }}
                       image={service.image || service.displayImage || '/placeholder-service.jpg'}
                       alt={service.serviceName || service.name}
                       sx={{
                         objectFit: 'cover',
                         width: '100%',
                         height: { xs: 120, sm: 140, md: 160 },
                         maxHeight: { xs: 120, sm: 140, md: 160 },
                         minHeight: { xs: 120, sm: 140, md: 160 }
                       }}
                       onError={(e) => {
                         e.target.src = '/placeholder-service.jpg';
                       }}
                     />
                     <CardContent sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1.5, sm: 2 } }}>
                       <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                         {service.serviceName || service.name}
                       </Typography>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                         <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                           by {service.providerName || service.provider?.businessName || 'Provider'}
                         </Typography>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           {(service.providerType === 'salon' || service.provider?.providerType === 'salon') ? (
                             <StoreIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                           ) : (
                             <PersonIcon sx={{ color: '#dc004e', fontSize: 16 }} />
                           )}
                           <Chip
                             label={(service.providerType === 'salon' || service.provider?.providerType === 'salon') ? 'Salon' : 'Pros'}
                             size="small"
                             sx={{
                               backgroundColor: (service.providerType === 'salon' || service.provider?.providerType === 'salon') ? '#1976d2' : '#dc004e',
                               color: 'white',
                               fontSize: '0.7rem',
                               height: 20,
                               '& .MuiChip-label': { px: 1 }
                             }}
                           />
                         </Box>
                       </Box>
                       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center' }}>
                             <TimeIcon sx={{ color: '#1976d2', fontSize: 14, mr: 0.5 }} />
                             <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                               {service.duration ? (
                                 <>
                                   {service.duration}
                                   <span style={{ fontSize: '0.65rem' }}> mins</span>
                                 </>
                               ) : (
                                 <>
                                   30
                                   <span style={{ fontSize: '0.65rem' }}> mins</span>
                                 </>
                               )}
                             </Typography>
                           </Box>
                           <Box sx={{ display: 'flex', alignItems: 'center' }}>
                             <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                               📍 {service.distance || 'Distance unavailable'}
                             </Typography>
                           </Box>
                         </Box>
                         <Box sx={{ display: 'flex', alignItems: 'center' }}>
                           {getGenderIcon(service.gender) || (
                             <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                               <Male sx={{ fontSize: 14, color: '#1976d2' }} />
                               <Female sx={{ fontSize: 14, color: '#e91e63' }} />
                             </Box>
                           )}
                         </Box>
                       </Box>
                       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                         <Box>
                           <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '1rem' }}>
                             {formatCurrency(service.price || service.basePrice || 0)}
                           </Typography>
                           {service.originalPrice && (
                             <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#999', fontSize: '0.8rem' }}>
                               {formatCurrency(service.originalPrice)}
                             </Typography>
                           )}
                         </Box>
                         {isItemInCart(service) ? (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             <IconButton
                               size="small"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(service);
                                 if (currentQuantity > 1) {
                                   updateItemQuantity(service, currentQuantity - 1);
                                 } else {
                                   // Remove from cart if quantity is 1
                                   const cartItem = items.find(item => 
                                     item.id === (service.id || service._id) && 
                                     item.itemType === 'service' && 
                                     item.providerId === (service.providerId || service.provider?._id)
                                   );
                                   if (cartItem) {
                                     removeFromCart(cartItem.cartItemId);
                                   }
                                 }
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>-</Typography>
                             </IconButton>
                             <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 20, textAlign: 'center' }}>
                               {getItemQuantityInCart(service)}
                             </Typography>
                             <IconButton
                               size="small"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(service);
                                 updateItemQuantity(service, currentQuantity + 1);
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>+</Typography>
                             </IconButton>
                           </Box>
                         ) : (
                           <Button
                             variant="contained"
                             size="small"
                             startIcon={<AddIcon />}
                             onClick={(e) => {
                               e.stopPropagation();
                               handleServiceAddToCart(service);
                             }}
                             sx={{ 
                               backgroundColor: '#1976d2',
                               '&:hover': { backgroundColor: '#1565c0' },
                               fontSize: { xs: '0.6rem', sm: '0.65rem' },
                               minHeight: '28px',
                               minWidth: '80px',
                               maxHeight: '28px',
                               padding: '4px 8px',
                               whiteSpace: 'nowrap',
                               textTransform: 'none'
                             }}
                           >
                             Add to Cart
                           </Button>
                         )}
                       </Box>
                     </CardContent>
                   </Card>
                 ))
               ) : (
                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 4 }}>
                   <Typography variant="h6" color="text.secondary">
                     No featured services available
                   </Typography>
                 </Box>
               )}
             </Box>
           </Box>

           {/* Popular Products Section */}
           <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                Popular Products
              </Typography>
              <Button 
                variant="contained" 
                size="small" 
                onClick={() => navigate('/store')}
                sx={{ 
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: 'white',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  fontWeight: 600,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 0.6 },
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.35)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                View All
              </Button>
            </Box>

             <Box sx={{ 
               display: 'flex',
               flexDirection: 'row',
               gap: 2,
               overflowX: 'auto',
               overflowY: 'hidden',
               WebkitOverflowScrolling: 'touch',
               scrollSnapType: 'x mandatory',
               pb: 2,
               '& > *': {
                 flexShrink: 0,
                 scrollSnapAlign: 'start'
               }
             }}>
               {products && products.length > 0 ? (
                 products.map((product, index) => (
                  <Card key={product.id || index} 
                    onClick={() => navigate(`/product/${product.id || index + 1}`)}
                    sx={{ 
                      minWidth: { xs: 200, sm: 240 }, 
                      maxWidth: { xs: 200, sm: 240 },
                      cursor: 'pointer', 
                      borderRadius: 3,
                      flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height={{ xs: 160, sm: 180, md: 200 }}
                        image={product.primaryImage || product.images?.[0] || product.image || '/placeholder-product.jpg'}
                        alt={product.productName}
                        sx={{
                          objectFit: 'cover',
                          width: '100%',
                          height: { xs: 160, sm: 180, md: 200 },
                          maxHeight: { xs: 160, sm: 180, md: 200 },
                          minHeight: { xs: 160, sm: 180, md: 200 }
                        }}
                        onError={(e) => {
                          e.target.src = '/placeholder-product.jpg';
                        }}
                      />
                      {!product.inStock && (
                        <Chip
                          label="Out of Stock"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: '#f44336',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                     <CardContent sx={{ 
                       py: 2,
                       display: 'flex',
                       flexDirection: 'column',
                       flex: 1
                     }}>
                       <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>
                         {product.productName}
                       </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                        by NISTYLEZ
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <StarIcon sx={{ color: '#ffd700', fontSize: 14, mr: 0.5 }} />
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            5
                          </Typography>
                        </Box>
                        {/* Discount Chip - Moved from image to rating line */}
                        {product.originalPrice && product.price && (() => {
                          // Extract numeric values from price strings (handle currency symbols)
                          const getNumericPrice = (priceStr) => {
                            if (typeof priceStr === 'number') return priceStr;
                            if (typeof priceStr === 'string') {
                              // Remove all non-numeric characters except decimal point
                              return parseFloat(priceStr.replace(/[^\d.]/g, '')) || 0;
                            }
                            return 0;
                          };
                          const originalPriceNum = getNumericPrice(product.originalPrice);
                          const priceNum = getNumericPrice(product.price);
                          const discountPercent = originalPriceNum > 0 
                            ? Math.round(((originalPriceNum - priceNum) / originalPriceNum) * 100)
                            : 0;
                          return discountPercent > 0 ? (
                            <Chip
                              label={`${discountPercent}% OFF`}
                              size="small"
                              sx={{
                                backgroundColor: '#ff6b35',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.6rem',
                                height: 18,
                                '& .MuiChip-label': { px: 1, py: 0 }
                              }}
                            />
                          ) : null;
                        })()}
                      </Box>
                       <Box sx={{ 
                         display: 'flex', 
                         alignItems: 'center', 
                         justifyContent: 'space-between', 
                         mt: 'auto',
                         mb: 1 
                       }}>
                         <Box>
                           <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '1rem' }}>
                             {formatCurrency(typeof product.price === 'number' ? product.price : parseFloat(String(product.price).replace(/[^\d.]/g, '')) || 0)}
                           </Typography>
                           {product.originalPrice && (
                             <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#999', fontSize: '0.8rem' }}>
                               {formatCurrency(typeof product.originalPrice === 'number' ? product.originalPrice : parseFloat(String(product.originalPrice).replace(/[^\d.]/g, '')) || 0)}
                             </Typography>
                           )}
                         </Box>
                         {isItemInCart(product) ? (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             <IconButton
                               size="small"
                               disabled={!product.inStock}
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(product);
                                 if (currentQuantity > 1) {
                                   updateItemQuantity(product, currentQuantity - 1);
                                 } else {
                                   // Remove from cart if quantity is 1
                                   const cartItem = items.find(item => 
                                     item.id === (product.id || product._id) && 
                                     item.itemType === 'product'
                                   );
                                   if (cartItem) {
                                     removeFromCart(cartItem.cartItemId);
                                   }
                                 }
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28,
                                 '&:disabled': { backgroundColor: '#f0f0f0' }
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>-</Typography>
                             </IconButton>
                             <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 20, textAlign: 'center' }}>
                               {getItemQuantityInCart(product)}
                             </Typography>
                             <IconButton
                               size="small"
                               disabled={!product.inStock}
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(product);
                                 updateItemQuantity(product, currentQuantity + 1);
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28,
                                 '&:disabled': { backgroundColor: '#f0f0f0' }
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>+</Typography>
                             </IconButton>
                           </Box>
                         ) : (
                           <Button
                             variant="contained"
                             size="small"
                             startIcon={<AddIcon />}
                             disabled={!product.inStock}
                             onClick={(e) => {
                               e.stopPropagation();
                               handleProductAddToCart(product);
                             }}
                             sx={{ 
                               backgroundColor: product.inStock ? '#1976d2' : '#ccc',
                               '&:hover': { backgroundColor: product.inStock ? '#1565c0' : '#ccc' },
                               fontSize: { xs: '0.65rem', sm: '0.7rem' },
                               minHeight: '32px',
                               minWidth: '100px',
                               maxHeight: '32px',
                               padding: '6px 12px',
                               whiteSpace: 'nowrap',
                               textTransform: 'none'
                             }}
                           >
                             Add to Cart
                           </Button>
                         )}
                       </Box>
                     </CardContent>
                   </Card>
                 ))
               ) : (
                 <Box sx={{ 
                   display: 'flex', 
                   flexDirection: 'column', 
                   alignItems: 'center', 
                   justifyContent: 'center',
                   minHeight: 200,
                   width: '100%'
                 }}>
                   <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                     No products available
                   </Typography>
                   <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                     Products will be loaded from the backend
                   </Typography>
                 </Box>
               )}
             </Box>
           </Box>

                     {/* Offers Section - Horizontal Scrollable - COMMENTED OUT */}
           {/* 
           {false && <Box sx={{ mb: 4 }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
               <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                 Special Offers
               </Typography>
             </Box>
             

             

             

             

             

             

             
             // Today's Offer - Only show if offers exist 
             {offers.today && offers.today.length > 0 && (
             <Box sx={{ mb: 3 }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                 <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                   🔥 Today's Offer
                 </Typography>
                 <Button 
                   variant="text" 
                   size="small" 
                   onClick={() => {
                     navigate('/offers', { state: { offerType: 'today' } });
                   }}
                   sx={{ color: '#d32f2f' }}
                 >
                   View All
                 </Button>
               </Box>
               <Box sx={{ 
                 display: 'flex', 
                 gap: 2, 
                 overflowX: 'auto', 
                 pb: 2,
                 '&::-webkit-scrollbar': { height: 8 },
                 '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' }
               }}>
                 {offers.today && offers.today.length > 0 ? (
                   offers.today.map((offer, index) => (
                   <Card key={offer._id || index} 
                     onClick={() => navigate(`/service/${offer._id || offer.id || `offer-${index + 1}`}`)}
                     sx={{ 
                       minWidth: 280, 
                       cursor: 'pointer', 
                       borderRadius: 3,
                       border: '2px solid #d32f2f',
                       flexShrink: 0,
                       boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                       transition: 'all 0.3s ease',
                       '&:hover': {
                         transform: 'translateY(-4px)',
                         boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                       }
                     }}
                   >
                     <Box sx={{ position: 'relative' }}>
                       <CardMedia
                         component="img"
                         height="180"
                         image={offer.imagePaths?.[0] || offer.displayImage || offer.image || '/placeholder-service.jpg'}
                         alt={offer.serviceName || offer.name || 'Service Offer'}
                       />
                       <Chip
                         label={`${offer.discount}% OFF`}
                         sx={{
                           position: 'absolute',
                           top: 8,
                           right: 8,
                           backgroundColor: '#d32f2f',
                           color: 'white',
                           fontWeight: 'bold',
                           fontSize: '0.7rem'
                         }}
                       />
                     </Box>
                     <CardContent sx={{ py: 2 }}>
                       // Service/Offer Name - Most Important 
                       <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem', color: '#333' }}>
                         {offer.serviceName || offer.name || 'Service Offer'}
                       </Typography>
                       

                       
                       // Provider Info 
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                         <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                           by {offer.providerName || 'Provider'}
                         </Typography>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           {offer.providerType === 'salon' ? (
                             <StoreIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                           ) : (
                             <PersonIcon sx={{ color: '#dc004e', fontSize: 16 }} />
                           )}
                           <Chip
                             label={offer.providerType === 'salon' ? 'Salon' : 'Pros'}
                             size="small"
                             sx={{
                               backgroundColor: offer.providerType === 'salon' ? '#1976d2' : '#dc004e',
                               color: 'white',
                               fontSize: '0.6rem',
                               height: 18,
                               '& .MuiChip-label': { px: 1 }
                             }}
                           />
                         </Box>
                       </Box>
                       
                       // Rating, Duration, Distance 
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                         <StarIcon sx={{ color: '#ffd700', fontSize: 14, mr: 0.5 }} />
                         <Typography variant="body2" sx={{ mr: 2, fontSize: '0.8rem' }}>
                           {offer.rating || offer.averageRating || 4.5}
                         </Typography>
                         <TimeIcon sx={{ color: '#666', fontSize: 14, mr: 0.5 }} />
                         <Typography variant="body2" color="text.secondary" sx={{ mr: 2, fontSize: '0.8rem' }}>
                           {offer.duration ? `${offer.duration} min` : '60 min'}
                         </Typography>
                         <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                           • {offer.distance || 'Distance unavailable'}
                         </Typography>
                       </Box>
                       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                         <Box>
                           <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f', fontSize: '1rem' }}>
                             {formatCurrency(offer.price)}
                           </Typography>
                           {offer.discount > 0 && (
                           <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#999', fontSize: '0.8rem' }}>
                               {formatCurrency(offer.price + (offer.price * offer.discount / 100))}
                           </Typography>
                           )}
                         </Box>
                         {isItemInCart(offer) ? (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             <IconButton
                               size="small"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(offer);
                                 if (currentQuantity > 1) {
                                   updateItemQuantity(offer, currentQuantity - 1);
                                 } else {
                                   // Remove from cart if quantity is 1
                                   const cartItem = items.find(item => 
                                     item.id === (offer.id || offer._id) && 
                                     item.itemType === 'offer'
                                   );
                                   if (cartItem) {
                                     removeFromCart(cartItem.cartItemId);
                                   }
                                 }
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>-</Typography>
                             </IconButton>
                             <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 20, textAlign: 'center' }}>
                               {getItemQuantityInCart(offer)}
                             </Typography>
                             <IconButton
                               size="small"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(offer);
                                 updateItemQuantity(offer, currentQuantity + 1);
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>+</Typography>
                             </IconButton>
                           </Box>
                         ) : (
                         <Button
                           variant="contained"
                           size="small"
                           startIcon={<AddIcon />}
                           onClick={(e) => {
                             e.stopPropagation();
                               handleOfferAddToCart(offer);
                           }}
                           sx={{ 
                             backgroundColor: '#d32f2f',
                             '&:hover': { backgroundColor: '#b71c1c' },
                             fontSize: '0.7rem'
                           }}
                         >
                           Add to Cart
                         </Button>
                         )}
                       </Box>
                       <Chip 
                         label={offer.validUntil ? `Valid until ${formatDateShort(offer.validUntil)}` : 'Limited Time'} 
                         size="small" 
                         sx={{ backgroundColor: '#ffebee', color: '#d32f2f', fontWeight: 'bold', fontSize: '0.6rem' }}
                       />
                     </CardContent>
                   </Card>
                 ))
               ) : (
                 <Box sx={{ 
                   display: 'flex', 
                   justifyContent: 'center', 
                   alignItems: 'center',
                   minHeight: 200,
                   width: '100%'
                 }}>
                   <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                     No offers available today
                   </Typography>
                   <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                     Check back later for new offers
                   </Typography>
               </Box>
               )}
             </Box>
             </Box>
             )}

               // This Week's Offer 
             <Box sx={{ mb: 3 }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                 <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                   ⭐ This Week's Offer
                 </Typography>
                 <Button 
                   variant="text" 
                   size="small" 
                   onClick={() => {
                     navigate('/offers', { state: { offerType: 'week' } });
                   }}
                   sx={{ color: '#1976d2' }}
                 >
                   View All
                 </Button>
               </Box>
               <Box sx={{ 
                 display: 'flex', 
                 gap: 2, 
                 overflowX: 'auto', 
                 pb: 2,
                 '&::-webkit-scrollbar': { height: 8 },
                 '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' }
               }}>
                 {[
                   {
                     id: 'offer_week_1',
                     serviceName: 'Complete Hair Treatment',
                     providerName: 'Elegance Studio',
                     providerType: 'salon',
                     duration: '90 min',
                     price: 60,
                     originalPrice: 100,
                     discount: '40% OFF',
                     image: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=300&h=200&fit=crop',
                     rating: 4.9,
                     distance: '1.8 km',
                     validUntil: 'This Week'
                   },
                   {
                     id: 'offer_week_2',
                     serviceName: 'Spa Package',
                     providerName: 'Beauty Haven',
                     providerType: 'salon',
                     duration: '120 min',
                     price: 80,
                     originalPrice: 150,
                     discount: '47% OFF',
                     image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=300&h=200&fit=crop',
                     rating: 4.7,
                     distance: '3.2 km',
                     validUntil: 'This Week'
                   },
                   {
                     id: 'offer_week_3',
                     serviceName: 'Premium Facial',
                     providerName: 'Glamour Salon',
                     providerType: 'salon',
                     duration: '75 min',
                     price: 45,
                     originalPrice: 75,
                     discount: '40% OFF',
                     image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=300&h=200&fit=crop',
                     rating: 4.8,
                     distance: null,
                     validUntil: 'This Week'
                   },
                   {
                     id: 'offer_week_4',
                     serviceName: 'Full Body Massage',
                     providerName: 'Maria Garcia',
                     providerType: 'pros',
                     duration: '90 min',
                     price: 70,
                     originalPrice: 120,
                     discount: '42% OFF',
                     image: 'https://images.unsplash.com/photo-1544161512f84f9c8137036?w=300&h=200&fit=crop',
                     rating: 4.9,
                     distance: '2.8 km',
                     validUntil: 'This Week'
                   },
                   {
                     id: 'offer_week_5',
                     serviceName: 'Hair Coloring Package',
                     providerName: 'Style Studio',
                     providerType: 'salon',
                     duration: '150 min',
                     price: 90,
                     originalPrice: 150,
                     discount: '40% OFF',
                     image: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=300&h=200&fit=crop',
                     rating: 4.7,
                     distance: '2.1 km',
                     validUntil: 'This Week'
                   },
                   {
                     id: 'offer_week_6',
                     serviceName: 'Bridal Package',
                     providerName: 'Sophie Brown',
                     providerType: 'pros',
                     duration: '180 min',
                     price: 120,
                     originalPrice: 200,
                     discount: '40% OFF',
                     image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=200&fit=crop',
                     rating: 4.9,
                     distance: '2.3 km',
                     validUntil: 'This Week'
                   },
                   {
                     id: 'offer_week_7',
                     serviceName: 'Anti-Aging Treatment',
                     providerName: 'Luxury Beauty',
                     providerType: 'salon',
                     duration: '120 min',
                     price: 85,
                     originalPrice: 140,
                     discount: '39% OFF',
                     image: 'https://images.unsplash.com/photo-1544161512-84f9c8137036?w=300&h=200&fit=crop',
                     rating: 4.8,
                     distance: '3.5 km',
                     validUntil: 'This Week'
                   },
                   {
                     id: 'offer_week_8',
                     serviceName: 'Men\'s Grooming Package',
                     providerName: 'Modern Cuts',
                     providerType: 'salon',
                     duration: '60 min',
                     price: 40,
                     originalPrice: 65,
                     discount: '38% OFF',
                     image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=300&h=200&fit=crop',
                     rating: 4.6,
                     distance: '1.3 km',
                     validUntil: 'This Week'
                   },
                   {
                     id: 'offer_week_9',
                     serviceName: 'Nail Extension Package',
                     providerName: 'Elite Salon',
                     providerType: 'salon',
                     duration: '120 min',
                     price: 55,
                     originalPrice: 90,
                     discount: '39% OFF',
                     image: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=300&h=200&fit=crop',
                     rating: 4.8,
                     distance: '2.8 km',
                     validUntil: 'This Week'
                   }
                 ].map((offer, index) => (
                   <Card key={index} 
                     onClick={() => navigate(`/service/${offer.id || `week-offer-${index + 1}`}`)}
                     sx={{ 
                       minWidth: 280, 
                       cursor: 'pointer', 
                       borderRadius: 3,
                       border: '2px solid #1976d2',
                       flexShrink: 0,
                       boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                       transition: 'all 0.3s ease',
                       '&:hover': {
                         transform: 'translateY(-4px)',
                         boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                       }
                     }}
                   >
                     <Box sx={{ position: 'relative' }}>
                       <CardMedia
                         component="img"
                         height="180"
                         image={offer.image}
                         alt={offer.serviceName}
                       />
                       <Chip
                         label={offer.discount}
                         sx={{
                           position: 'absolute',
                           top: 8,
                           right: 8,
                           backgroundColor: '#1976d2',
                           color: 'white',
                           fontWeight: 'bold',
                           fontSize: '0.7rem'
                         }}
                       />
                     </Box>
                     <CardContent sx={{ py: 2 }}>
                       <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>
                         {offer.serviceName}
                       </Typography>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                         <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                           by {offer.providerName}
                         </Typography>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           {offer.providerType === 'salon' ? (
                             <StoreIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                           ) : (
                             <PersonIcon sx={{ color: '#dc004e', fontSize: 16 }} />
                           )}
                           <Chip
                             label={offer.providerType === 'salon' ? 'Salon' : 'Pros'}
                             size="small"
                             sx={{
                               backgroundColor: offer.providerType === 'salon' ? '#1976d2' : '#dc004e',
                               color: 'white',
                               fontSize: '0.6rem',
                               height: 18,
                               '& .MuiChip-label': { px: 1 }
                             }}
                           />
                         </Box>
                       </Box>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                         <StarIcon sx={{ color: '#ffd700', fontSize: 14, mr: 0.5 }} />
                         <Typography variant="body2" sx={{ mr: 2, fontSize: '0.8rem' }}>
                           {offer.rating}
                         </Typography>
                         <TimeIcon sx={{ color: '#666', fontSize: 14, mr: 0.5 }} />
                         <Typography variant="body2" color="text.secondary" sx={{ mr: 2, fontSize: '0.8rem' }}>
                           {offer.duration}
                         </Typography>
                         <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                           • {offer.distance}
                         </Typography>
                       </Box>
                       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                         <Box>
                           <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '1rem' }}>
                             {formatCurrency(offer.price)}
                           </Typography>
                           <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#999', fontSize: '0.8rem' }}>
                             {formatCurrency(offer.originalPrice)}
                           </Typography>
                         </Box>
                         {isItemInCart(offer) ? (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             <IconButton
                               size="small"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(offer);
                                 if (currentQuantity > 1) {
                                   updateItemQuantity(offer, currentQuantity - 1);
                                 } else {
                                   // Remove from cart if quantity is 1
                                   const cartItem = items.find(item => 
                                     item.id === (offer.id || offer._id) && 
                                     item.itemType === 'offer'
                                   );
                                   if (cartItem) {
                                     removeFromCart(cartItem.cartItemId);
                                   }
                                 }
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>-</Typography>
                             </IconButton>
                             <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 20, textAlign: 'center' }}>
                               {getItemQuantityInCart(offer)}
                             </Typography>
                             <IconButton
                               size="small"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(offer);
                                 updateItemQuantity(offer, currentQuantity + 1);
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>+</Typography>
                             </IconButton>
                           </Box>
                         ) : (
                         <Button
                           variant="contained"
                           size="small"
                           startIcon={<AddIcon />}
                           onClick={(e) => {
                             e.stopPropagation();
                               handleOfferAddToCart(offer);
                           }}
                           sx={{ 
                             backgroundColor: '#1976d2',
                             '&:hover': { backgroundColor: '#1565c0' },
                             fontSize: '0.7rem'
                           }}
                         >
                           Add to Cart
                         </Button>
                         )}
                       </Box>
                       <Chip 
                         label={offer.validUntil} 
                         size="small" 
                         sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold', fontSize: '0.6rem' }}
                       />
                     </CardContent>
                   </Card>
                 ))}
               </Box>
             </Box>

                         // This Month's Offer 
             <Box sx={{ mb: 3 }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                 <Typography variant="h6" sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
                   🌟 This Month's Offer
                 </Typography>
                 <Button 
                   variant="text" 
                   size="small" 
                   onClick={() => {
                     navigate('/offers', { state: { offerType: 'month' } });
                   }}
                   sx={{ color: '#7b1fa2' }}
                 >
                   View All
                 </Button>
               </Box>
               <Box sx={{ 
                 display: 'flex', 
                 gap: 2, 
                 overflowX: 'auto', 
                 pb: 2,
                 '&::-webkit-scrollbar': { height: 8 },
                 '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 4 },
                 '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' }
               }}>
                 {[
                   {
                     id: 'offer_month_1',
                     serviceName: 'Premium Makeup Package',
                     providerName: 'Sarah Johnson',
                     providerType: 'pros',
                     duration: '120 min',
                     price: 100,
                     originalPrice: 200,
                     discount: '50% OFF',
                     image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=200&fit=crop',
                     rating: 4.9,
                     distance: '1.2 km',
                     validUntil: 'This Month'
                   },
                   {
                     id: 'offer_month_2',
                     serviceName: 'Full Body Massage',
                     providerName: 'Maria Garcia',
                     providerType: 'pros',
                     duration: '90 min',
                     price: 70,
                     originalPrice: 140,
                     discount: '50% OFF',
                     image: 'https://images.unsplash.com/photo-1544161512-84f9c8137036?w=300&h=200&fit=crop',
                     rating: 4.8,
                     distance: '2.8 km',
                     validUntil: 'This Month'
                   },
                   {
                     id: 'offer_month_3',
                     serviceName: 'Luxury Spa Treatment',
                     providerName: 'Beauty Haven',
                     providerType: 'salon',
                     duration: '150 min',
                     price: 120,
                     originalPrice: 240,
                     discount: '50% OFF',
                     image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=300&h=200&fit=crop',
                     rating: 4.9,
                     distance: '3.2 km',
                     validUntil: 'This Month'
                   },
                   {
                     id: 'offer_month_4',
                     serviceName: 'Complete Hair & Makeup',
                     providerName: 'Lisa Chen',
                     providerType: 'pros',
                     duration: '180 min',
                     price: 150,
                     originalPrice: 300,
                     discount: '50% OFF',
                     image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&h=200&fit=crop',
                     rating: 4.8,
                     distance: '1.5 km',
                     validUntil: 'This Month'
                   },
                   {
                     id: 'offer_month_5',
                     serviceName: 'VIP Hair Treatment',
                     providerName: 'Elegance Studio',
                     providerType: 'salon',
                     duration: '120 min',
                     price: 110,
                     originalPrice: 220,
                     discount: '50% OFF',
                     image: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=300&h=200&fit=crop',
                     rating: 4.9,
                     distance: '1.8 km',
                     validUntil: 'This Month'
                   },
                   {
                     id: 'offer_month_6',
                     serviceName: 'Premium Facial Package',
                     providerName: 'Glamour Salon',
                     providerType: 'salon',
                     duration: '90 min',
                     price: 80,
                     originalPrice: 160,
                     discount: '50% OFF',
                     image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=300&h=200&fit=crop',
                     rating: 4.8,
                     distance: null,
                     validUntil: 'This Month'
                   },
                   {
                     id: 'offer_month_7',
                     serviceName: 'Luxury Nail Package',
                     providerName: 'Elite Salon',
                     providerType: 'salon',
                     duration: '120 min',
                     price: 75,
                     originalPrice: 150,
                     discount: '50% OFF',
                     image: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=300&h=200&fit=crop',
                     rating: 4.8,
                     distance: '2.8 km',
                     validUntil: 'This Month'
                   },
                   {
                     id: 'offer_month_8',
                     serviceName: 'Executive Grooming',
                     providerName: 'Modern Cuts',
                     providerType: 'salon',
                     duration: '90 min',
                     price: 60,
                     originalPrice: 120,
                     discount: '50% OFF',
                     image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=300&h=200&fit=crop',
                     rating: 4.6,
                     distance: '1.3 km',
                     validUntil: 'This Month'
                   },
                   {
                     id: 'offer_month_9',
                     serviceName: 'Wellness Massage Package',
                     providerName: 'Olivia Davis',
                     providerType: 'pros',
                     duration: '120 min',
                     price: 90,
                     originalPrice: 180,
                     discount: '50% OFF',
                     image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=200&fit=crop',
                     rating: 4.7,
                     distance: '1.8 km',
                     validUntil: 'This Month'
                   }
                 ].map((offer, index) => (
                   <Card key={index} 
                     onClick={() => navigate(`/service/${offer.id || `month-offer-${index + 1}`}`)}
                     sx={{ 
                       minWidth: 280, 
                       cursor: 'pointer', 
                       borderRadius: 3,
                       border: '2px solid #7b1fa2',
                       flexShrink: 0,
                       boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                       transition: 'all 0.3s ease',
                       '&:hover': {
                         transform: 'translateY(-4px)',
                         boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                       }
                     }}
                   >
                     <Box sx={{ position: 'relative' }}>
                       <CardMedia
                         component="img"
                         height="180"
                         image={offer.image}
                         alt={offer.serviceName}
                       />
                       <Chip
                         label={offer.discount}
                         sx={{
                           position: 'absolute',
                           top: 8,
                           right: 8,
                           backgroundColor: '#7b1fa2',
                           color: 'white',
                           fontWeight: 'bold',
                           fontSize: '0.7rem'
                         }}
                       />
                     </Box>
                     <CardContent sx={{ py: 2 }}>
                       <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.9rem' }}>
                         {offer.serviceName}
                       </Typography>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                         <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                           by {offer.providerName}
                         </Typography>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           {offer.providerType === 'salon' ? (
                             <StoreIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                           ) : (
                             <PersonIcon sx={{ color: '#dc004e', fontSize: 16 }} />
                           )}
                           <Chip
                             label={offer.providerType === 'salon' ? 'Salon' : 'Pros'}
                             size="small"
                             sx={{
                               backgroundColor: offer.providerType === 'salon' ? '#1976d2' : '#dc004e',
                               color: 'white',
                               fontSize: '0.6rem',
                               height: 18,
                               '& .MuiChip-label': { px: 1 }
                             }}
                           />
                         </Box>
                       </Box>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                         <StarIcon sx={{ color: '#ffd700', fontSize: 14, mr: 0.5 }} />
                         <Typography variant="body2" sx={{ mr: 2, fontSize: '0.8rem' }}>
                           {offer.rating}
                         </Typography>
                         <TimeIcon sx={{ color: '#666', fontSize: 14, mr: 0.5 }} />
                         <Typography variant="body2" color="text.secondary" sx={{ mr: 2, fontSize: '0.8rem' }}>
                           {offer.duration}
                         </Typography>
                         <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                           • {offer.distance}
                         </Typography>
                       </Box>
                       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                         <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#7b1fa2', fontSize: '1rem' }}>
                            {formatCurrency(typeof offer.price === 'number' ? offer.price : parseFloat(String(offer.price).replace(/[^\d.]/g, '')) || 0)}
                          </Typography>
                          <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#999', fontSize: '0.8rem' }}>
                            {formatCurrency(typeof offer.originalPrice === 'number' ? offer.originalPrice : parseFloat(String(offer.originalPrice).replace(/[^\d.]/g, '')) || 0)}
                          </Typography>
                         </Box>
                         {isItemInCart(offer) ? (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             <IconButton
                               size="small"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(offer);
                                 if (currentQuantity > 1) {
                                   updateItemQuantity(offer, currentQuantity - 1);
                                 } else {
                                   // Remove from cart if quantity is 1
                                   const cartItem = items.find(item => 
                                     item.id === (offer.id || offer._id) && 
                                     item.itemType === 'offer'
                                   );
                                   if (cartItem) {
                                     removeFromCart(cartItem.cartItemId);
                                   }
                                 }
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>-</Typography>
                             </IconButton>
                             <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 20, textAlign: 'center' }}>
                               {getItemQuantityInCart(offer)}
                             </Typography>
                             <IconButton
                               size="small"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 const currentQuantity = getItemQuantityInCart(offer);
                                 updateItemQuantity(offer, currentQuantity + 1);
                               }}
                               sx={{ 
                                 backgroundColor: '#f5f5f5',
                                 '&:hover': { backgroundColor: '#e0e0e0' },
                                 width: 28,
                                 height: 28
                               }}
                             >
                               <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>+</Typography>
                             </IconButton>
                           </Box>
                         ) : (
                         <Button
                           variant="contained"
                           size="small"
                           startIcon={<AddIcon />}
                           onClick={(e) => {
                             e.stopPropagation();
                               handleOfferAddToCart(offer);
                           }}
                           sx={{ 
                             backgroundColor: '#7b1fa2',
                             '&:hover': { backgroundColor: '#6a1b9a' },
                             fontSize: '0.7rem'
                           }}
                         >
                           Add to Cart
                         </Button>
                         )}
                       </Box>
                       <Chip 
                         label={offer.validUntil} 
                         size="small" 
                         sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2', fontWeight: 'bold', fontSize: '0.6rem' }}
                       />
                     </CardContent>
                   </Card>
                 ))}
               </Box>
             </Box>
          </Box>
*/}
          {/* Hygiene & Safety Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
              Hygiene & Safety Standards
            </Typography>
            <Box 
              ref={hygieneScrollRef}
              onMouseEnter={() => setIsHygieneScrollPaused(true)}
              onMouseLeave={() => setIsHygieneScrollPaused(false)}
              sx={{ 
                display: 'flex', 
                gap: 2, 
                overflowX: 'auto', 
                pb: 2,
                scrollBehavior: 'smooth',
                '&::-webkit-scrollbar': { height: 8 },
                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 4 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 4 },
                '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' }
              }}>
              {[
                { icon: '🧼', title: 'Sanitized Equipment', description: 'All tools are thoroughly sanitized', color: '#e3f2fd' },
                { icon: '😷', title: 'Masked Professionals', description: 'Staff wear masks for your safety', color: '#f3e5f5' },
                { icon: '🧴', title: 'Disposable Items', description: 'Single-use items for hygiene', color: '#e8f5e8' },
                { icon: '🌡️', title: 'Temperature Check', description: 'Regular health screenings', color: '#fff3e0' },
                { icon: '🧽', title: 'Clean Environment', description: 'Regular disinfection protocols', color: '#fce4ec' },
                { icon: '👐', title: 'Hand Sanitizer', description: 'Available at all locations', color: '#e0f2f1' },
                { icon: '🛡️', title: 'Safety Protocols', description: 'Strict safety guidelines followed', color: '#f1f8e9' },
                { icon: '✨', title: 'Premium Quality', description: 'High-quality materials used', color: '#e8eaf6' }
              ].map((item, index) => (
                <Card key={index} sx={{ 
                  minWidth: 200,
                  textAlign: 'center', 
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}dd 100%)`,
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}>
                  <CardContent sx={{ py: 2, px: 2 }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: '50%', 
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <Typography variant="h4" sx={{ fontSize: '2rem' }}>
                        {item.icon}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.9rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
           

          {/* Follow Us on Social */}
          <Box sx={{ mb: 4, pb: 8, border: '2px solid #f97316', borderRadius: 2, background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', overflow: 'hidden' }}>
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                Follow Us on Social
            </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Stay connected and get the latest updates
              </Typography>
                  
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                {[
                  { name: 'Facebook', icon: FacebookIcon, color: '#1877f2', url: 'https://www.facebook.com/nistylezau/' },
                  { name: 'X', icon: XIcon, color: '#000000', url: 'https://x.com/nistylez' },
                  { name: 'Instagram', icon: InstagramIcon, color: '#e4405f', url: 'https://www.instagram.com/nistylez_au' },
                  { name: 'LinkedIn', icon: LinkedInIcon, color: '#0077b5', url: 'https://www.linkedin.com/in/nisha-shah-5b6310355?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app' },
                  { name: 'YouTube', icon: YouTubeIcon, color: '#ff0000', url: 'https://www.youtube.com/@nistylez-au' }
                ].map((social) => (
                  <SocialIconButton
                    key={social.name}
                  sx={{ 
                      backgroundColor: social.color,
                      color: 'white',
                    '&:hover': { 
                        backgroundColor: social.color,
                      transform: 'scale(1.1)',
                      }
                    }}
                    onClick={() => window.open(social.url, '_blank')}
                  >
                    <social.icon />
                  </SocialIconButton>
              ))}
            </Box>
                    
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3 }}>
                Join our community for exclusive offers and updates
              </Typography>
          </Box>
          </Box>
        </Box>
        
        {/* Bottom Navigation */}
        <BottomNavigation />

        {/* Filter Dialog */}
        <Dialog 
          open={filterDialogOpen} 
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Search Filters
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {/* Service Mode - Hidden as per requirement */}

              {/* Provider Type */}
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Provider Type
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                        <Checkbox
                          defaultChecked={providerTypes.includes('salon')}
                          onChange={(e) => {
                          //  console.log('Salon: ', e.target.checked);
                            if (e.target.checked) {
                              setProviderTypes(prev => [...prev, 'salon']);
                            } else {
                              setProviderTypes(prev => prev.filter(type => type !== 'salon'));
                            }
                          }}
                        />
                    }
                    label="Salon"
                  />
                  <FormControlLabel
                    control={
                        <Checkbox
                          defaultChecked={providerTypes.includes('pros')}
                          onChange={(e) => {
                           // console.log('Pros: ', e.target.checked);
                            if (e.target.checked) {
                              setProviderTypes(prev => [...prev, 'pros']);
                            } else {
                              setProviderTypes(prev => prev.filter(type => type !== 'pros'));
                            }
                          }}
                        />
                    }
                    label="Pros"
                  />
                </FormGroup>
              </FormControl>

              {/* Categories */}
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Categories
                </Typography>
                <FormGroup>
                  {categories.length > 0 ? categories.map((category) => (
                    <FormControlLabel
                      key={category._id || category.name}
                      control={
                        <Checkbox
                          defaultChecked={selectedCategories.includes(category.name)}
                          onChange={(e) => {
                          //  console.log(`Category: ${category.name} - ${e.target.checked}`);
                            if (e.target.checked) {
                              setSelectedCategories(prev => [...prev, category.name]);
                            } else {
                              setSelectedCategories(prev => prev.filter(cat => cat !== category.name));
                            }
                          }}
                        />
                      }
                      label={category.name}
                    />
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      No categories available
                    </Typography>
                  )}
                </FormGroup>
              </FormControl>

              {/* Distance */}
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Distance
                </Typography>
                {/* Distance slider - always visible, no radio buttons */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Up to {distance} km
                  </Typography>
                  <Slider
                    value={distance}
                    onChange={(e, newValue) => setDistance(newValue)}
                    min={1}
                    max={100}
                    marks={[
                      { value: 1, label: '1km' },
                      { value: 25, label: '25km' },
                      { value: 50, label: '50km' },
                      { value: 75, label: '75km' },
                      { value: 100, label: '100km' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
              </FormControl>

              {/* Gender */}
              <FormControl sx={{ mb: 3, width: '100%' }}>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  label="Gender"
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Unisex">Unisex</MenuItem>
                </Select>
              </FormControl>

              {/* Rating */}
              <FormControl sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Minimum Rating: {rating}★
                </Typography>
                <Slider
                  value={rating}
                  onChange={(e, newValue) => setRating(newValue)}
                  min={1}
                  max={5}
                  step={0.5}
                  marks={[
                    { value: 1, label: '1★' },
                    { value: 3, label: '3★' },
                    { value: 5, label: '5★' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </FormControl>

                             {/* Sort By */}
               <FormControl sx={{ mb: 3, width: '100%' }}>
                 <InputLabel>Sort By</InputLabel>
                 <Select
                   value={sortBy}
                   onChange={(e) => setSortBy(e.target.value)}
                   label="Sort By"
                 >
                   <MenuItem value="distance-near">Distance: Nearest to Farthest</MenuItem>
                   <MenuItem value="distance-far">Distance: Farthest to Nearest</MenuItem>
                   <MenuItem value="price-low">Price: Low to High</MenuItem>
                   <MenuItem value="price-high">Price: High to Low</MenuItem>
                   <MenuItem value="rating-high">Rating: High to Low</MenuItem>
                   <MenuItem value="rating-low">Rating: Low to High</MenuItem>
                 </Select>
               </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={resetFilters}
              color="secondary"
            >
              Reset Filters
            </Button>
            <Button onClick={() => setFilterDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                // Apply filters logic here
                setFilterDialogOpen(false);
              }}
              variant="contained"
              sx={{ backgroundColor: '#1a237e' }}
            >
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Location Dialog with Google Maps */}
        <EnhancedLocationDialog
          open={locationDialogOpen}
          onClose={() => setLocationDialogOpen(false)}
          currentAddress={selectedAddress || location}
          onLocationSelect={(locationData) => {
            setSelectedAddress(locationData.address);
            setLocation(locationData.address);
            
            // Save location data to localStorage
            const locationInfo = {
              address: locationData.address,
              fullAddress: locationData.address,
              latitude: locationData.lat,
              longitude: locationData.lng,
              city: locationData.address ? locationData.address.split(',')[0] : '', // Extract city from address with null check
              type: locationData.type
            };
            localStorage.setItem('userLocation', JSON.stringify(locationInfo));
            localStorage.setItem('userAddress', locationData.address);
            
            // Refresh data with new location
          //  console.log('📍 Location selected from dialog, refreshing data with new coordinates...');
            fetchAllHomeData();
            
            setLocationDialogOpen(false);
          }}
        />

        {/* Sign Up Prompt Modal for Guest Users */}
        <SignUpPromptModal
          open={showSignUpPrompt}
          onClose={() => setShowSignUpPrompt(false)}
          featureName={restrictedFeature}
        />

        {/* AI Try-On Dialog */}
        <TryOnDialog
          open={tryOnDialogOpen}
          onClose={() => setTryOnDialogOpen(false)}
          category={tryOnCategory}
        />
      </Box>
    );
  };

export default Home;
